import { useCallback, useEffect, useState } from 'react';
import { api } from './api/client';
import { date } from './lib/format';

const STATUS_META = [
  { key: 'to_be_scheduled', label: 'To be scheduled', tone: 'violet' },
  { key: 'waiting_parts', label: 'Waiting parts', tone: 'amber' },
  { key: 'in_process', label: 'In process', tone: 'blue' },
  { key: 'on_hold', label: 'On-hold', tone: 'slate' },
  { key: 'completed', label: 'Completed', tone: 'green' },
];
const typeLabel = (t) => ({ sim_repair: 'Sim repair', sim_move: 'Simulator move', sim_upgrade: 'Simulator upgrade', site_visit: 'Site visit', flight: 'Flight', other: 'Other' }[t] || t);
const isOverdue = (d, status) => d && status !== 'completed' && new Date(d) < new Date(new Date().toDateString());
const REFRESH_MS = 60000;
const STALE_MIN = 5; // show the red pill if no successful refresh in this many minutes

export default function KioskPublicApp() {
  const token = (window.location.pathname.match(/^\/kiosk\/([^/]+)/) || [])[1] || '';
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(new Date());
  const [lastUpdated, setLastUpdated] = useState(null); // ms of last successful load

  const load = useCallback(() => {
    if (!token) { setError('No display token.'); return; }
    api(`/public/kiosk?token=${encodeURIComponent(token)}`)
      .then((d) => { setData(d); setLastUpdated(Date.now()); setError(null); })
      .catch((e) => {
        // Keep the last good board on screen if we already have one — staleness is shown via the pill.
        // Only surface a full error screen when we've never managed to load.
        setError(e?.status === 401 ? 'This display link is not valid.' : 'Could not load the board.');
      });
  }, [token]);

  useEffect(() => { load(); const t = setInterval(load, REFRESH_MS); return () => clearInterval(t); }, [load]);
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);

  // Error screen only before the first successful load; after that the board stays up and may go stale.
  if (!data && error) {
    return <div className="kx kx--msg"><div><div className="kx__logo">Flightdeck</div><p className="kx__err">{error}</p></div></div>;
  }
  if (!data) {
    return <div className="kx kx--msg"><div className="kx__logo">Flightdeck</div></div>;
  }

  const jobs = data.jobs || [];
  const tasks = data.tasks || [];
  const clock = now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const today = now.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
  const staleMin = lastUpdated ? Math.floor((now.getTime() - lastUpdated) / 60000) : 0;
  const stale = staleMin >= STALE_MIN;
  const updatedAt = lastUpdated ? new Date(lastUpdated).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '—';

  return (
    <div className="kx">
      <div className="kx__bar">
        <div className="kx__brand">Flightdeck <span className="kx__brandsub">Task Board</span></div>
        <div className="kx__when">
          <div className="kx__clock">{clock}</div>
          {stale
            ? <div className="kx__stale">● Stale · no update in {staleMin} min</div>
            : <div className="kx__date">{today} · updated {updatedAt}</div>}
        </div>
      </div>

      <div className="kx__board">
        <div className="kx__col kx__col--jobs">
          <div className="kx__coltit kx__coltit--blue">Upcoming jobs<span className="kx__n">{jobs.length}</span></div>
          <div className="kx__cards">
            {jobs.length === 0 ? <div className="kx__colempty">—</div> : jobs.map((j) => (
              <div className="kx__jobc" key={j.id}>
                <div className="kx__jobtop"><span className="kx__jobnum">{j.job_number}</span>{j.business_short && <span className="kx__jobbiz">{j.business_short}</span>}</div>
                <div className="kx__jobcust">{j.customer_name || j.title || 'Job'}</div>
                <div className="kx__jobmeta">{typeLabel(j.type)}{j.scheduled_date ? ` · ${date(j.scheduled_date)}` : ''}</div>
                {j.location && <div className="kx__jobmeta kx__jobmeta--dim">{j.location}</div>}
                {j.crew && <div className="kx__jobcrew">{j.crew}</div>}
              </div>
            ))}
          </div>
        </div>
        {STATUS_META.map((col) => {
          const list = tasks.filter((t) => t.status === col.key);
          return (
            <div className="kx__col" key={col.key}>
              <div className={`kx__coltit kx__coltit--${col.tone}`}>{col.label}<span className="kx__n">{list.length}</span></div>
              <div className="kx__cards">
                {list.map((t) => (
                  <div className="kx__card" key={t.id}>
                    <div className="kx__cardtitle">{t.title}</div>
                    <div className="kx__cardrow">
                      <span className="kx__owner">{t.owner_name}</span>
                      {t.customer && <span className="kx__cust">{t.customer}</span>}
                    </div>
                    <div className="kx__cardrow">
                      {t.due_date && <span className={'kx__due' + (isOverdue(t.due_date, t.status) ? ' kx__due--over' : '')}>Due {date(t.due_date)}</span>}
                      {t.parts_to_order > 0 && <span className="kx__partsbadge">{t.parts_to_order} to order</span>}
                    </div>
                  </div>
                ))}
                {list.length === 0 && <div className="kx__colempty">—</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
